import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { ApiRoutes, Authentication } from '@sb/library/api';
import { CancellableObservable, DecodeHTML, DesignBlockScope, IFrameUserActionSubmitMessage, PaymentContext, PaymentContextScope, PaymentDetails, PaymentProcessingScope, PaymentSchoolInfo, PaymentSourceMode, WebsiteMode, WebsitePageScope } from '@sb/library/base';
import { AdditionalStudentSignupInputItem, CalculateSurchargeInputItem, CalendarBookingDisplayItem, CalendarBookingSearchOptions, CustomFieldValueInputItem, DisplayItemCollection, ErrorCode, FieldMode, FormAffinity, ParentSignupInputItem, PaymentProcessorDisplayItem, PaymentSourceInputItem, PaymentSourceRegistrationNextAction, PaymentSourceType, SignupInputItem, SignupPaymentType, SignupResultItem, StripePaymentSourceInputItem, SurchargeResultItem, TagDisplayItem, TeacherNameDisplayItem, TimeZoneInfoDisplayItem, TimeZoneSearchOptions, WebsiteBlockSignupFormDisplayItem, WebsiteBlockSignupFormType, WebsiteHostingType, WebsiteRenderContextTeacherNameDisplayItem, WebsiteRenderMode } from '@sb/library/data-models';
import { SBDate } from '@sb/library/localization';
import { PaymentErrorDialogComponent, PaymentErrorDialogScope } from '@sb/library/payments';
import { RecaptchaComponent } from '@sb/library/recaptcha';
import { StepperComponent } from '@sb/library/stepper';
import { Observable, ReplaySubject, Subject, map, of, tap } from 'rxjs';
import { DesignBlockIframeBaseViewComponent } from '../design-block-iframe-base-view.component';
import { SignupFormDesignBlockBookingComponent } from './booking/signup-form-design-block-booking.component';
import { SignupFormDesignBlockConfirmationComponent } from './confirmation/signup-form-design-block-confirmation.component';
import { BookingMode, CalendarBookingFrontendInputItem, PreFetchedSlots, SignupData, SignupSteps, SignupStudentMode, StudentSignupFrontendInputItem } from './signup-form.definitions';
import { SignupFormDesignBlockStudentComponent } from './student/signup-form-design-block-student.component';
import { SignupFormDesignBlockSubjectsComponent } from './subjects/signup-form-design-block-subjects.component';
import { SignupFormDesignBlockTeachersComponent } from './teachers/signup-form-design-block-teachers.component';

@Component({
    selector: 'lib-signup-form-design-block',
    templateUrl: './signup-form-design-block.component.html',
    styleUrls: ['./signup-form-design-block.component.scss'],
})
export class SignupFormDesignBlockComponent extends DesignBlockIframeBaseViewComponent implements PaymentContextScope, PaymentProcessingScope, DesignBlockScope, PaymentErrorDialogScope {
    
    override Scope = this.SetScope({
        Implements: [PaymentContextScope, PaymentProcessingScope, DesignBlockScope, PaymentErrorDialogScope],
        Requires: WebsitePageScope
    });

    @ViewChild('recaptcha') recaptchaRef!: RecaptchaComponent;
    @ViewChild(SignupFormDesignBlockStudentComponent, { static: false }) studentStep!: SignupFormDesignBlockStudentComponent;
    @ViewChild(SignupFormDesignBlockBookingComponent, { static: false }) bookingStep?: SignupFormDesignBlockBookingComponent;
    @ViewChild(SignupFormDesignBlockTeachersComponent, { static: false }) teacherStep?: SignupFormDesignBlockTeachersComponent;
    @ViewChild(SignupFormDesignBlockConfirmationComponent, { static: false }) confirmationStep?: SignupFormDesignBlockConfirmationComponent;
    @ViewChild(SignupFormDesignBlockSubjectsComponent, { static: false}) subjectStep?: SignupFormDesignBlockSubjectsComponent;
    @ViewChild(StepperComponent<typeof SignupSteps>, {static: false}) stepper?: StepperComponent<typeof SignupSteps>;
    @ViewChild('signupViewableRef', {static: false}) signupViewableRef!: ElementRef<HTMLElement>;
    @ViewChild('signupBlock', { read: ElementRef, static: false })
    set signupBlock(content: ElementRef) {
        if (content) {
            this.DetectChanges();
        }
    }

    @Input() websiteBlockData!: WebsiteBlockSignupFormDisplayItem;
    @Output() submitSignupClicked: EventEmitter<SignupInputItem> = new EventEmitter();

    readonly context: PaymentContext = PaymentContext.SignupWidget;
    readonly paymentDetails = new PaymentDetails();

    readonly triggerUpdateAmountWithSurcharge = new ReplaySubject<SurchargeResultItem>(1);
    readonly triggerProcessPaymentSource = new Subject<void>();
    readonly triggerProcessPaymentSourceNextStep = new Subject<SignupResultItem>();
    readonly triggerReenablePaymentProcessorForm = new Subject<void>();

    private _paymentProcessing: boolean = false;
    private _paymentSourceComponentEnabled: boolean = true;
    private _schoolInfo!: PaymentSchoolInfo;

    private _students: StudentSignupFrontendInputItem[] = [];
    private _initialStudent?: StudentSignupFrontendInputItem;
    private _currentStudent?: StudentSignupFrontendInputItem;
    private _parent!: ParentSignupInputItem;
    private _formCustomFieldsForAdditionalStudent: CustomFieldValueInputItem[] = [];
    private _signupInputItem: SignupInputItem | null = null;
    private _paymentProcessorErrorMessage: string | null = null;
    private _signupStudentMode: SignupStudentMode = SignupStudentMode.Anonymous;
    private _bookingRequestItem: CalendarBookingFrontendInputItem | null = null;
    private _bookingMode: BookingMode = BookingMode.Default;
    private _paymentSource: PaymentSourceInputItem | null = null;
    private _paymentSourceType: PaymentSourceType = PaymentSourceType.CreditCard;
    private _supportsStoredPayments: boolean = false;
    private _eventPriceHasChanged: boolean = false;
    private _amountToPay: number = 0;
    private _surchargeData: SurchargeResultItem[] = [];
    private _showPaymentStep: boolean = false; // we need to keep this separate because Stripe needs time for the DOM to initialize its own component
    private _currentTimezone?: TimeZoneInfoDisplayItem;

    private _teachers: WebsiteRenderContextTeacherNameDisplayItem[] = [];
    private _filteredTeachers: WebsiteRenderContextTeacherNameDisplayItem[] = [];
    private _subjectTags: TagDisplayItem[] = [];
    private _preFetchedSlots?: PreFetchedSlots;
    private _teachersBySubjectMap: Record<string, WebsiteRenderContextTeacherNameDisplayItem[]> = {};
    private _teachersByCategoryNameMap: Record<string, WebsiteRenderContextTeacherNameDisplayItem[]> = {};
    private _bookingSlots: CalendarBookingDisplayItem[] = [];
    private _availableTeachers: WebsiteRenderContextTeacherNameDisplayItem[] = [];
    private _loading: boolean = true;
    private _totalItemsLength: number = 0;

    private _showWidgetMisconfigured: boolean = false;
    private _showWidgetAvailabilityMisconfigured: boolean = false;
    private _signupWidgetInView: boolean = false;

    override ngOnInit() {
        super.ngOnInit();

        if (this.Scope.renderContext.SignupFormRenderContext) {
            this.SetTeachersAndSubjects();

            this._supportsStoredPayments = this.Scope.renderContext.SignupFormRenderContext!.PaymentProcessor !== null
                                        && this.Scope.renderContext.SignupFormRenderContext!.PaymentProcessor.StoredPaymentMethodsSupported;
        }

        if (this.Scope.renderContext.RenderMode == WebsiteRenderMode.Widget) {
            this._schoolInfo = new PaymentSchoolInfo({
                ID: this.websiteBlockData.SchoolID!,
                PaymentSurchargeCreditCard: this.Scope.renderContext.SignupFormRenderContext!.PaymentSurchargeCreditCard,
                PaymentSurchargeACH: this.Scope.renderContext.SignupFormRenderContext!.PaymentSurchargeACH,
                CurrencyCode: this.Scope.renderContext.SignupFormRenderContext!.CurrencyCode!,
                ForceAutoPay: this.Scope.renderContext.SignupFormRenderContext!.ForceAutoPay
            });
        }
    }

    ngAfterViewInit(): void {
        if (this.isBookingWidget) {
            if (this.signupViewableRef) {
                const observer = new IntersectionObserver(entries => {
                    entries.forEach(entry => {
                        if (!this._signupWidgetInView && entry.isIntersecting) {
                            this._signupWidgetInView = true;
                            this.FetchBookingSlotData();
                        }
                    });
                }, {
                    threshold: 0.1
                });
                observer.observe(this.signupViewableRef.nativeElement);
            }
        } else {
            this._loading = false;
        }
    }

    PaymentProcessorReady(): void {
        this._paymentProcessing = false;

        if (this.stepper!.GetCurrentStep() === SignupSteps.Confirmation) {
            this.stepper!.Previous();
            this.confirmationStep!.EnableSubmitButton();
        }
    }

    PaymentSourceTypeChanged(paymentSourceType: PaymentSourceType) {
        this._paymentSourceType = paymentSourceType;
        const surcharge = this._surchargeData.find(d => d.PaymentSourceType === paymentSourceType)!;
        this.triggerUpdateAmountWithSurcharge.next(surcharge);
    }

    private SetTeachersAndSubjects() {
        if (this.websiteBlockData.TeacherIDs?.length > 0) {
            this._teachers = this.Scope.renderContext.SignupFormRenderContext!.PreferredTeachers.filter(teacher => this.websiteBlockData.TeacherIDs.includes(teacher.ID!));
        } else {
            this._teachers = this.Scope.renderContext.SignupFormRenderContext!.PreferredTeachers;
        }

        this._subjectTags = this.Scope.renderContext.SignupFormRenderContext!.SubjectTags.orderByKey('Name');
        this._teachers = this._teachers.orderByKey('Name');
        this._filteredTeachers = this._teachers;

        //Visibility misconfigured
        if (((this.isBookingWidget || this.websiteBlockData.AdditionalFields?.PreferredTeacher === FieldMode.Required) && this._teachers.length === 0) || this.websiteBlockData.TeacherIDs?.length === 0) {
            this._showWidgetMisconfigured = true;
        }

        //Availability misconfigured
        if (this.isAvailabilityBookingWidget
            && this.Scope.websiteMode === WebsiteMode.Preview
            && this.IsAvailabilityMisconfigured()) {
            this._showWidgetAvailabilityMisconfigured = true;
        }

    }

    private FetchBookingSlotData(): void {
        const body = this.BuildCalendarBookingSearchOptions();
        const canSetTimeZone = this.Scope.renderContext.SignupFormRenderContext?.CanSetTimeZone;

        if (canSetTimeZone) {
            this.ResolveTimeZoneID().subscribe((timeZoneID: string | null) => {
                if (timeZoneID) {
                    this.CallBookingSlot(body, timeZoneID).subscribe(data => {
                        this.ProcessBookingSlotData(data);
                    });
                } else {
                    this.CallBookingSlot(body).subscribe(data => {
                        this.ProcessBookingSlotData(data);
                    });
                }
 
            });
        } else {
            this.CallBookingSlot(body).subscribe(data => {
                this.ProcessBookingSlotData(data);
            });
        }
    }

    private BuildCalendarBookingSearchOptions(): CalendarBookingSearchOptions {
        const maxDate = SBDate.Today().AddDays(this.Scope.renderContext.SignupFormRenderContext?.AdvanceBookingLimitDays! + 1);

        return new CalendarBookingSearchOptions({
            ExistingEvents: this.isCalendarBookingWidget,
            TeacherIDs: this.teachers.map(t => t.ID!),
            StartDate: SBDate.Today().ToISO(),
            EndDate: maxDate.ToISO(),
            AllDay: false,
            EventCategoryIDs: this.websiteBlockData.EventCategoryIDs!,
            StudentIDs: [],
            MakeUpCreditRequired: false
        });
    }

    private ResolveTimeZoneID(): Observable<string | null> {
        const existingTimeZoneID = this._bookingRequestItem?.TimezoneID;

        if (existingTimeZoneID) {
            return of(existingTimeZoneID);
        }

        const timeZoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;

        if (!timeZoneName) {
            return of(null);
        }

        return ApiRoutes.Localization.TimeZones.Search.Call({
            Body: new TimeZoneSearchOptions({
                TimeZoneID: timeZoneName
            })
        }).pipe(tap((data => { this._currentTimezone = data; })), map((data: TimeZoneInfoDisplayItem) => data.ID!));
    }

    private CallBookingSlot(body: CalendarBookingSearchOptions, timeZoneID?: string): CancellableObservable<DisplayItemCollection<CalendarBookingDisplayItem>> {
        const isLive = this.Scope.websiteMode === WebsiteMode.Live;
        const hasSchoolAndBlockIDs = this.websiteBlockData.SchoolID && this.websiteBlockData.ID;
        const queryParams = timeZoneID ? { TimeZone: timeZoneID } : undefined;

        if (!isLive || !hasSchoolAndBlockIDs) {
            return ApiRoutes.Calendar.BookingSlots.GetAll.Call({
                Body: body,
                ExtraQueryParameters: queryParams
            });
        } else {
            return ApiRoutes.Calendar.BookingSlots.Get.Call({
                Parameter1: this.websiteBlockData.SchoolID!,
                Parameter2: this.websiteBlockData.ID!,
                Body: body,
                ExtraQueryParameters: queryParams
            });
        }
    }

    private ProcessBookingSlotData(data: DisplayItemCollection<CalendarBookingDisplayItem>): void {
        this._bookingSlots = data.ItemSubset;

        if (!this._preFetchedSlots) {
            this._preFetchedSlots = { BookingSlots: [] };
        }
        this._preFetchedSlots!.BookingSlots = data.ItemSubset;

        this._preFetchedSlots = {...this._preFetchedSlots!}; // trigger change detection for pre-fetched slots
        const teacherMap = new Map<string, WebsiteRenderContextTeacherNameDisplayItem>();

        this._bookingSlots.forEach(b => {
            const teacherNameDisplayItem = this.teachers.find(t => t.ID! === b.OriginalTeacher!.ID!);
            const teacher = new WebsiteRenderContextTeacherNameDisplayItem({
                ID: b.OriginalTeacher!.ID,
                Name: b.OriginalTeacher!.Name,
                HasProfilePicture: teacherNameDisplayItem?.HasProfilePicture,
                BiographyBody: teacherNameDisplayItem?.BiographyBody,
                FirstName: teacherNameDisplayItem?.FirstName,
                LastName: teacherNameDisplayItem?.LastName,
                ProfileHeadshotURL: teacherNameDisplayItem?.ProfileHeadshotURL,
                SpokenLanguages: teacherNameDisplayItem?.SpokenLanguages
            });

            if (b.OriginalTeacher && !teacherMap.has(b.OriginalTeacher.ID!)) {
                teacherMap.set(b.OriginalTeacher.ID!, teacher);
            }

            if (this._teachersByCategoryNameMap[b.EventCategory!.Name!]) {
                if (!this._teachersByCategoryNameMap[b.EventCategory!.Name!].find(t => t.ID === teacher.ID)) {
                    this._teachersByCategoryNameMap[b.EventCategory!.Name!].push(teacher);
                }
            } else {
                this._teachersByCategoryNameMap[b.EventCategory!.Name!] = [teacher];
            }
        });

        for (const category in this._teachersByCategoryNameMap) {
            this._teachersByCategoryNameMap[category] = this._teachersByCategoryNameMap[category].orderByKey('Name');
        }

        this._availableTeachers = Array.from(teacherMap.values()).orderByKey('Name');

        this._availableTeachers.forEach(teacher => {
            const teacherSubjectTags = this._subjectTags.filter(tag => tag.TeacherIDs.includes(teacher.ID!));
            if (teacherSubjectTags.length > 0) {
                teacherSubjectTags.forEach(tag => {
                    this._teachersBySubjectMap[tag.Name!] ??= [];
                    if (!this._teachersBySubjectMap[tag.Name!].some(t => t.ID === teacher.ID)) {
                        this._teachersBySubjectMap[tag.Name!].push(teacher);
                    }
                });
            } else {
                this._teachersBySubjectMap.Other ??= [];
                this._teachersBySubjectMap.Other.push(teacher);
            }
        });

        const categories = Object.keys(this._teachersByCategoryNameMap);
        const subjects = Object.keys(this._teachersBySubjectMap);
        this._totalItemsLength = this.isCalendarBookingWidget ? categories.length : subjects.length;
        this._loading = false;
    }

    SubjectSignupClicked(data: PreFetchedSlots): void {
        if (this.isAvailabilityBookingWidget) {
            this._filteredTeachers = data.SubjectTag ? this._availableTeachers.filter(t => data.SubjectTag!.TeacherIDs.includes(t.ID!)) : [...this._availableTeachers];
        } else {
            this._filteredTeachers = data.Category !== undefined ? this._teachersByCategoryNameMap[data.Category] : [...this._availableTeachers];
        }

        //Order Matters here
        setTimeout(() => {
            this.bookingStep?.Reset();
            this.stepper?.Next();
            this._preFetchedSlots = data;
        })
    }

    TeacherSignupClicked(data: PreFetchedSlots): void {
        // filter teachers again based on data + subject
        if (data.TeacherID) {
            this._filteredTeachers = this._availableTeachers.filter(t => t.ID === data.TeacherID);
        } else {
            // skipped
            this._filteredTeachers = data.SubjectTag ? this._availableTeachers.filter(t => data.SubjectTag!.TeacherIDs.includes(t.ID!)) : [...this._availableTeachers];
        }

        //Order Matters here
        setTimeout(() => {
            this.bookingStep?.Reset();
            if (this.isBookingWidget) {
                this.stepper?.GoToStep(SignupSteps.Booking);
            } else {
                this.stepper?.Next();
            }
            this._preFetchedSlots = data;
        })
    }

    BookClicked(request: CalendarBookingFrontendInputItem): void {
        this._bookingRequestItem = new CalendarBookingFrontendInputItem({
            Duration: request.Duration,
            EventCategoryID: request.EventCategoryID,
            EventCategory: request.EventCategory,
            EventID: request.EventID,
            EventLocationID: request.EventLocationID,
            EventLocation: request.EventLocation,
            StartDate: request.StartDate,
            EndDate: request.EndDate,
            TeacherID: request.TeacherID,
            Teacher: request.Teacher,
            Mode: request.Mode,
            EventPrice: request.EventPrice,
            TimezoneID: request.TimezoneID,
            SubjectTagID: this._preFetchedSlots?.SubjectTag?.ID ?? '',
        });

        this._bookingMode = request.Mode;

        if (this.isCalendarBookingWidget) {
            this._subjectTags = this.Scope.renderContext.SignupFormRenderContext!.SubjectTags.filter(tag => tag.TeacherIDs.some(teacherID => teacherID === this._bookingRequestItem!.TeacherID));
        }

        switch (request.Mode) {
            case BookingMode.Default:
                this.stepper?.Next();
                break;
            case BookingMode.ChooseNewSlot:

                const collectsPayments = this._supportsStoredPayments && this.websiteBlockData.PaymentType !== null;
                this._eventPriceHasChanged = this._currentStudent ? this._currentStudent.BookingRequest?.EventPrice !== request.EventPrice : false;
                //There is no confirm page if you dont have Payments;
                if (this.signupStudentMode === SignupStudentMode.Anonymous) {
                    if (this._eventPriceHasChanged && collectsPayments) {
                        // this.paymentStep?.EnableForm();
                        this.confirmationStep?.EnableSubmitButton();
                        this.stepper?.GoToStep(SignupSteps.Payment); // Re-enter credit card details
                    } else if (!this._eventPriceHasChanged && collectsPayments) {
                        this.confirmationStep?.EnableSubmitButton();
                        this.stepper?.GoToStep(SignupSteps.Confirmation); // go to confirm page
                    } else {
                        // Submit the Anonymous Student
                        this._initialStudent!.BookingRequest = this.bookingRequestItem;
                        this.SubmitAnonymousStudent();
                    }
                } else {
                    if (collectsPayments) {
                        this.confirmationStep?.EnableSubmitButton();
                        this.stepper?.GoToStep(SignupSteps.Confirmation); // Go to confirm page
                    } else {
                        //Submit the Additional Student
                        this._currentStudent!.BookingRequest = this.bookingRequestItem;
                        this.SubmitAdditionalStudent(this._currentStudent!);
                    }
                }
                break;
        }

    }

    BookStepCancelClicked(): void {
        //Coming from AdditionalStudent
        this.stepper?.GoToStep(SignupSteps.ThankYou);
    }

    BookStepPreviousClicked(): void {
        if (!(this._filteredTeachers.length > 1)) {
            // this means we skipped the teacher picker as the subject/category only has 1 teacher
            this._filteredTeachers = this.teachers;
        }
        setTimeout(() => {
            this.subjectStep?.Reset();
            this.teacherStep?.Reset();
            this.stepper?.Previous();
        })
    }

    StudentStepSignupClicked(inputItem: SignupData): void {
        inputItem.Student.BookingRequest = this._bookingRequestItem;
        this._currentStudent =  inputItem.Student;
        this._currentStudent.BookingRequest = this._bookingRequestItem;
        inputItem.Student.Parent = inputItem.Parent;
        this._students.push(inputItem.Student);

        if (inputItem.Mode === SignupStudentMode.Anonymous) {
            this.HandleAnonymousStudentSignup(inputItem);
        } else {
            this.HandleAdditionalStudentSignup(inputItem);
        }

        // Fire setter on thank you page to update student
        setTimeout(() => this._students = [...this._students]);
    }

    private HandleAnonymousStudentSignup(inputItem: SignupData): void {
        this._initialStudent = inputItem.Student;
        this._initialStudent.BookingRequest = this._bookingRequestItem;
        this._parent = inputItem.Parent;

        if (this.websiteBlockData.PaymentType && this._supportsStoredPayments) {
            this._amountToPay = this.GetAmountToPay();

            let surcharge: Observable<SurchargeResultItem[]>;
            if (this._amountToPay) {
                surcharge = ApiRoutes.PaymentSource.CalculateSurcharge.Call({
                    Parameter: this.websiteBlockData.SchoolID!,
                    Body: new CalculateSurchargeInputItem({
                        Subtotal: this._amountToPay
                    })
                });
            } else {
                surcharge = of([new SurchargeResultItem({ PaymentSourceType: PaymentSourceType.CreditCard })]);
            }

            surcharge.subscribe(data => {
                this._surchargeData = data;

                const cardSurcharge = data.find(d => d.PaymentSourceType === PaymentSourceType.CreditCard)!;
                this.triggerUpdateAmountWithSurcharge.next(cardSurcharge);
                this.stepper?.Next();

                this._showPaymentStep = true;
            });
        } else {
            this.HandleCaptcha();
        }
    }

    private GetAmountToPay(): number {
        switch (this.websiteBlockData.PaymentType) {
            case SignupPaymentType.Fees:
                return (this.websiteBlockData.RegistrationFeeAmount ?? 0) + (this._bookingRequestItem?.EventPrice ?? 0);
            case SignupPaymentType.Deposit:
                return this.websiteBlockData.DepositAmount ?? 0;
            default:
                return 0;
        }
    }

    SubmitPaymentClicked() {
        this.triggerProcessPaymentSource.next();
    }

    private HandleAdditionalStudentSignup(inputItem: SignupData): void {
        if (this.websiteBlockData.PaymentType && this._supportsStoredPayments && this._bookingRequestItem?.EventPrice! > 0) {
            //Calculate additional student surcharge
            ApiRoutes.PaymentSource.CalculateSurcharge.Call({
                Parameter: this.websiteBlockData.SchoolID!,
                Body: new CalculateSurchargeInputItem({
                    Subtotal: (this._bookingRequestItem?.EventPrice! + this.websiteBlockData.RegistrationFeeAmount!)
                })
            }).subscribe(data => {
                const amountWithSurcharge = data.find(d => d.PaymentSourceType == this._paymentSourceType)!;
                this._paymentSource!.Surcharge = amountWithSurcharge.Surcharge;
                this._paymentSource!.Total = amountWithSurcharge.Total;
                this.stepper?.Next();
            });
        } else {
            this.SubmitAdditionalStudent(inputItem.Student);
        }
    }

    private SubmitAdditionalStudent(student: StudentSignupFrontendInputItem): void {
        const inputItem = new AdditionalStudentSignupInputItem(student);
        inputItem.WebsiteBlockID = this.websiteBlockData.ID,
        inputItem.CustomFieldValues = inputItem.CustomFieldValues.concat(this._formCustomFieldsForAdditionalStudent);
        ApiRoutes.Website.Signup.Additional.Call({
            Body: inputItem,
            ExtraQueryParameters: { TimeZone: this._bookingRequestItem?.TimezoneID }
        }).subscribe({
            next: () => {
                this.stepper?.GoToStep(SignupSteps.ThankYou);
                this.RunTrackingScriptFormComplete();
            },
            error: (error: typeof ApiRoutes.Website.Signup.Additional.Error) => {
                this.ActionSignupErrors(error);
            },
        });
    }

    private SubmitAnonymousStudent(): void {
        this._signupInputItem = new SignupInputItem({
            WebsiteBlockID: this.websiteBlockData.ID,
            Parent: new ParentSignupInputItem({
                ...this._parent,
            }),
            Student: this._initialStudent,
            Captcha: this.recaptchaRef.resolvedRecaptchaToken,
        });

        //call API to submit form
        ApiRoutes.Website.Signup.Initial.Call({
            Body: this._signupInputItem,
            ExtraQueryParameters: { TimeZone: this._bookingRequestItem?.TimezoneID }
        }).subscribe({
            next: result => {
                this.DetectChanges();
                Authentication.SetFromJWT(result.Jwt!);
                this.stepper?.GoToStep(SignupSteps.ThankYou);
                this.RunTrackingScriptFormComplete();
            },
            error: (error: typeof ApiRoutes.Website.Signup.Initial.Error) => {
                this.ActionSignupErrors(error);
            },
        });
    }

    StudentStepSecondaryActionClicked(): void {
        if (this.isBookingWidget) {
            this.bookingStep!.EnableStep();
            this.stepper?.Previous();
        } else {
            this.stepper?.GoToStep(SignupSteps.ThankYou);
        }
    }

    private HandlePaymentSource(paymentSource: PaymentSourceInputItem | null): void {
        this._paymentProcessorErrorMessage = null;
        if (paymentSource !== null) {
            this._signupInputItem = new SignupInputItem({
                WebsiteBlockID: this.websiteBlockData.ID,
                Parent: new ParentSignupInputItem({
                    ...this._parent,
                }),
                Student: this._initialStudent,
                PaymentSource: paymentSource,
                Captcha: this.recaptchaRef.resolvedRecaptchaToken,
            });
            this.HandleCaptcha();
        } else {
            //SCA failure
            this.recaptchaRef.ResetCaptchaToken();
        }
    }

    private SubmitAnonymousStudentWithPayment() {
        //when original input item was made, we didnt have token yet, so we need to
        //now add it if it is available, or null otherwise.
        this._signupInputItem!.Captcha = this.recaptchaRef.resolvedRecaptchaToken;

        ApiRoutes.Website.Signup.Initial.Call({
            Body: this._signupInputItem!,
            ExtraQueryParameters: { TimeZone: this._bookingRequestItem?.TimezoneID }
        }).subscribe({
            next: result => {
                switch (result.NextAction) {
                    case PaymentSourceRegistrationNextAction.None:
                        this.RunTrackingScriptFormComplete();
                        this.stepper?.GoToStep(SignupSteps.ThankYou);
                        Authentication.SetFromJWT(result.Jwt!);
                        break;
                    case PaymentSourceRegistrationNextAction.UseStripeSdk:
                        this.triggerProcessPaymentSourceNextStep.next(result);
                        break;
                }
            },
            error: (error: typeof ApiRoutes.Website.Signup.Initial.Error) => {
                this.ActionSignupErrors(error);
            },
        });
    }

    private RunTrackingScriptFormComplete(): void {
        this.parentIframe?.sendMessage(new IFrameUserActionSubmitMessage({
            FormTitle: DecodeHTML(this.websiteBlockData.Header!),
            FormType: 'signup'
        }));
    }

    ProcessPaymentSourceComplete(paymentSource: PaymentSourceInputItem) {
        this._paymentSource = paymentSource;
        this.stepper?.Next();
    }

    PaymentStepBackClicked(): void {
        this._students.pop();
        this.stepper?.Previous();
        this.studentStep.EnableForm();
    }

    ConfirmationStepSubmitClicked(): void {
        switch (this.stepper?.GetPreviousStep()) {
            case SignupSteps.Booking:
                //We are coming from choosing a new slot, we need to check if it's for the initial student or the additional students
                if (this._signupStudentMode === SignupStudentMode.Anonymous) {
                    this._initialStudent!.BookingRequest = this._bookingRequestItem;
                    this.HandlePaymentSource(this._paymentSource);
                } else {
                    this._currentStudent!.BookingRequest = this._bookingRequestItem;
                    this.SubmitAdditionalStudent(this._currentStudent!);
                }
                break;
            case SignupSteps.Student:
                //We can only come from Student if it's additional student
                this.SubmitAdditionalStudent(this._currentStudent!);
                break;
            case SignupSteps.Payment:
                // We always come from payment
                this.HandlePaymentSource(this._paymentSource);
                break;
        }
    }

    ConfirmationStepPreviousClicked(): void {
        switch (this.stepper?.GetPreviousStep()) {
            case SignupSteps.Booking:
                this.stepper?.GoToStep(SignupSteps.Booking);
                this.bookingStep?.EnableStep();
                break;
            case SignupSteps.Student:
                this._students.pop();
                this.stepper?.Previous();
                this.studentStep.EnableForm();
                break;
            case SignupSteps.Payment:
                this.stepper?.Previous();
                break;
        }
    }

    CaptchaSucceeded() {
        if (this.stepper!.GetCurrentStep() === SignupSteps.Student) {
            this.SubmitAnonymousStudent();
        } else {
            this.SubmitAnonymousStudentWithPayment();
        }
    }

    CaptchaError() {
        if (this.stepper!.GetCurrentStep() === SignupSteps.Student) {
            this.studentStep.EnableForm();
        } else {
            this.confirmationStep!.EnableSubmitButton();
        }
    }

    ThankYouStepAddStudentClicked(): void {
        if (this.isBookingWidget) {
            this.bookingStep?.EnableStep();
            this.FetchBookingSlotData();
        }
        this._signupStudentMode = SignupStudentMode.AdditionalStudent;
        this._formCustomFieldsForAdditionalStudent = this._signupInputItem!.Student!.CustomFieldValues.filter(field =>
            this.websiteBlockData.CustomFields?.some(f => f.ID === field.FieldID && f.Affinity === FormAffinity.Form)
        );

        if (this.studentStep) {
            this.studentStep.signupStudentMode = this._signupStudentMode;
            this.studentStep.Reset();

        }
        this.confirmationStep?.EnableSubmitButton();
        this.subjectStep?.Reset();
        this.stepper?.GoToStart();
    }

    ThankYouStepDoneClicked(): void {
        this._filteredTeachers = this._teachers;
        this._signupStudentMode = SignupStudentMode.Anonymous;
        this._formCustomFieldsForAdditionalStudent = [];
        if (this.studentStep) {
            this.studentStep.signupStudentMode = this._signupStudentMode;
            this.studentStep.Reset();
        }
        this.subjectStep?.Reset();
        this.confirmationStep?.EnableSubmitButton();
        this.stepper?.GoToStart();
        Authentication.SetFromJWT(this.Scope.renderContext.SignupFormRenderContext?.Jwt!);
        this._students = [];
        if (this.isBookingWidget) {
            this.bookingStep?.EnableStep();
            this.FetchBookingSlotData();
            setTimeout(() => this.bookingStep!.ResetSlotPicker());
        }
        this._showPaymentStep = false;
        // Reset payment step
        this._paymentSourceComponentEnabled = false;
        setTimeout(() => this._paymentSourceComponentEnabled = true);
    }

    //After SCA verification, we need to resubmit, but if captcha was needed, we need a new token!
    PaymentSourceNextStepComplete(intentID: string): void {
        (this._signupInputItem!.PaymentSource as StripePaymentSourceInputItem).IntentID = intentID;
        this.SubmitAnonymousStudentWithPayment();
    }

    PaymentErrorDismissed() {
        this.confirmationStep?.EnableSubmitButton();
        this.triggerReenablePaymentProcessorForm.next();
    }

    private ActionSignupErrors(error: typeof ApiRoutes.Website.Signup.Initial.Error | typeof ApiRoutes.Website.Signup.Additional.Error) {
        if (error.Status == 429) {
            this.OpenDialog429ApiError().subscribe(_ => {
                this.confirmationStep?.EnableSubmitButton();
                this.triggerReenablePaymentProcessorForm.next();
            });
        } else if (error.Response.ErrorCode === ErrorCode.OnlinePaymentError) {
            this.CreateDialog(PaymentErrorDialogComponent, { IsProcessorError: true, ErrorMessage: error.Response.ErrorMessage! });
            this.recaptchaRef.ResetCaptchaToken();
        } else if (error.Response.ErrorCode === ErrorCode.Captcha) {
            this.recaptchaRef.ResetCaptchaToken();
            this.Scope.renderContext.SignupFormRenderContext!.CaptchaRequired = true;
            this.HandleCaptcha();
        } else if (error.Response.ErrorCode === ErrorCode.BookingFailed) {
            this.bookingStep?.Reset();
            this.stepper?.GoToStep(SignupSteps.Booking);
            this._bookingMode = BookingMode.AlreadyBooked;
            this.confirmationStep?.EnableSubmitButton();
        } else {
            throw error;
        }
    }

    private HandleCaptcha(): void {
        this.recaptchaRef.GetCaptcha(this.Scope.renderContext.SignupFormRenderContext!.CaptchaRequired!);
    }

    private IsAvailabilityMisconfigured(): boolean {
        if (!this.Scope.renderContext.SignupFormRenderContext?.PreviewTeachersVisibleHasBookingAvailability) {
            return false;
        }

        let atLeastOneTeacherAvailable = this.Scope.renderContext.SignupFormRenderContext!.PreviewTeachersVisibleHasBookingAvailability.length > 0;
        let atLeastOneTeacherAvailableIfSpecified = this.websiteBlockData.TeacherIDs === null
            ? true
            : this.Scope.renderContext.SignupFormRenderContext!.PreviewTeachersVisibleHasBookingAvailability.some(teacher => this.websiteBlockData.TeacherIDs?.includes(teacher.ID!))

        return !atLeastOneTeacherAvailable || !atLeastOneTeacherAvailableIfSpecified;
    }

    get kind(): WebsiteBlockSignupFormType {
        return this.websiteBlockData.SignupFormType;
    }

    get teachers(): WebsiteRenderContextTeacherNameDisplayItem[] {
        return this._teachers;
    }

    get subjectTags(): TagDisplayItem[] {
        return this._subjectTags;
    }

    get students(): StudentSignupFrontendInputItem[] {
        return this._students;
    }

    get parent(): ParentSignupInputItem {
        return this._parent;
    }

    get paymentProcessorErrorMessage(): string | null {
        return this._paymentProcessorErrorMessage;
    }

    get WebsiteBlockSignupFormType(): typeof WebsiteBlockSignupFormType {
        return WebsiteBlockSignupFormType;
    }

    get WebsiteHostingType(): typeof WebsiteHostingType {
        return WebsiteHostingType;
    }

    get signupStudentMode(): SignupStudentMode {
        return this._signupStudentMode;
    }

    get SignupStudentMode(): typeof SignupStudentMode {
        return SignupStudentMode;
    }

    get isAvailabilityBookingWidget(): boolean {
        return this.websiteBlockData.SignupFormType == WebsiteBlockSignupFormType.AvailabilityBookingDefault || this.websiteBlockData.SignupFormType == WebsiteBlockSignupFormType.AvailabilityBookingAccentBackground;
    }

    get isCalendarBookingWidget(): boolean {
        return this.websiteBlockData.SignupFormType == WebsiteBlockSignupFormType.CalendarBookingDefault || this.websiteBlockData.SignupFormType == WebsiteBlockSignupFormType.CalendarBookingAccentBackground;
    }

    get isBookingWidget(): boolean {
        return this.isAvailabilityBookingWidget || this.isCalendarBookingWidget;
    }

    get SignupSteps(): typeof SignupSteps {
        return SignupSteps;
    }

    get SignupPaymentType(): typeof SignupPaymentType {
        return SignupPaymentType;
    }

    get bookingMode(): BookingMode {
        return this._bookingMode;
    }

    get currentStudent(): StudentSignupFrontendInputItem {
        return this._currentStudent!;
    }

    get supportsStoredPayments(): boolean {
        return this._supportsStoredPayments;
    }

    get bookingRequestItem(): CalendarBookingFrontendInputItem | null {
        return this._bookingRequestItem;
    }

    get paymentSource(): PaymentSourceInputItem | null {
        return this._paymentSource;
    }

    get showWidgetMisconfigured(): boolean {
        return this._showWidgetMisconfigured;
    }

    get showWidgetAvailabilityMisconfigured(): boolean {
        return this._showWidgetAvailabilityMisconfigured;
    }

    get showPaymentStep(): boolean {
        return this._showPaymentStep;
    }

    get filteredTeachers(): WebsiteRenderContextTeacherNameDisplayItem[] {
        return this._filteredTeachers;
    }

    get preFetchedSlots(): PreFetchedSlots | undefined {
        return this._preFetchedSlots;
    }

    get teachersBySubjectMap(): Record<string, WebsiteRenderContextTeacherNameDisplayItem[]> {
        return this._teachersBySubjectMap;
    }

    get teachersByCategoryNameMap(): Record<string, WebsiteRenderContextTeacherNameDisplayItem[]> {
        return this._teachersByCategoryNameMap;
    }

    get totalItemsLength(): number {
        return this._totalItemsLength;
    }

    get loading(): boolean {
        return this._loading
    }

    get bookingSlots(): CalendarBookingDisplayItem[] {
        return this._bookingSlots;
    }

    get currentTimezone(): TimeZoneInfoDisplayItem | undefined {
        return this._currentTimezone;
    }

    get paymentProcessing(): boolean {
        return this._paymentProcessing;
    }

    get mode(): PaymentSourceMode {
        return (!this.websiteBlockData.PaymentType || this._amountToPay === 0) ? PaymentSourceMode.Add : PaymentSourceMode.Payment;
    }

    get paymentProcessor(): PaymentProcessorDisplayItem { 
        return this.Scope.renderContext.SignupFormRenderContext!.PaymentProcessor!;
    }

    get schoolInfo(): PaymentSchoolInfo {
        return this._schoolInfo;
    }

    get paymentSourceComponentEnabled(): boolean {
        return this._paymentSourceComponentEnabled;
    }

    get PaymentSourceMode(): typeof PaymentSourceMode {
        return PaymentSourceMode;
    }

    get PaymentContext(): typeof PaymentContext {
        return PaymentContext;
    }
}
